import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useGetOcOrderHistoryQuery } from "../../store/apis/ocApi";
import {
  OcOrderChangeMap,
  OcOrderHistoryAction,
  OcOrderHistoryEntry,
} from "../../types/oc";
import { formatDateES, formatEUR } from "../../utils/oc/ocCalc";

interface OcChangeLogDialogProps {
  open: boolean;
  /** Null while no row is selected — the query stays skipped. */
  ocId: string | null;
  ocNumber?: string;
  onClose: () => void;
}

// ---- Field labels ----------------------------------------------------------
// The backend sends a flat diff: top-level fields plus "client.*" / "issuer.*"
// for the frozen snapshots (see buildOcChanges in ocOrderService).
const FIELD_LABEL_KEYS: Record<string, string> = {
  mode: "oc.changelog.fields.mode",
  ocDate: "oc.date",
  campaign: "oc.campaign",
  issuerCompanyId: "oc.issuer",
  clientId: "oc.changelog.fields.clientId",
  clientBillingId: "oc.changelog.fields.clientBillingId",
  items: "oc.changelog.fields.items",
  totals: "oc.totals.total",
  showCompanyContact: "oc.changelog.fields.showCompanyContact",
  showClientContact: "oc.changelog.fields.showClientContact",
  compactMode: "oc.changelog.fields.compactMode",
  duplicatedFrom: "oc.changelog.fields.duplicatedFrom",
};

const PARTY_FIELD_LABEL_KEYS: Record<string, string> = {
  label: "oc.changelog.fields.label",
  legalName: "oc.legalName",
  taxId: "oc.taxId",
  address: "oc.address",
  city: "oc.city",
  zip: "oc.zip",
  country: "oc.country",
  phone: "oc.phone",
  email: "oc.email",
  website: "oc.web",
  paymentTerms: "oc.paymentMethod",
  isRBA: "oc.changelog.fields.isRBA",
  code: "oc.changelog.fields.code",
  logoUrl: "oc.changelog.fields.logoUrl",
};

const ACTION_COLORS: Record<
  OcOrderHistoryAction,
  "success" | "info" | "warning"
> = {
  CREATE: "success",
  DUPLICATE: "info",
  UPDATE: "warning",
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

function fieldLabel(field: string, t: Translate): string {
  const [prefix, ...rest] = field.split(".");
  if ((prefix === "client" || prefix === "issuer") && rest.length > 0) {
    const subKey = PARTY_FIELD_LABEL_KEYS[rest.join(".")];
    const owner = prefix === "client" ? t("oc.client") : t("oc.issuer");
    return `${owner} · ${subKey ? t(subKey) : rest.join(".")}`;
  }
  const key = FIELD_LABEL_KEYS[field];
  return key ? t(key) : field;
}

function formatValue(field: string, value: unknown, t: Translate): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean")
    return value ? t("common.yes") : t("common.no");
  if (field === "mode") return t(`oc.modeShort.${String(value)}`);
  if (field === "ocDate") return formatDateES(String(value));
  if (field === "totals") {
    const total =
      typeof value === "number"
        ? value
        : (value as { tot?: number; cant?: number }).tot;
    return formatEUR(total ?? 0);
  }
  if (field === "items" && Array.isArray(value)) {
    const concepts = value
      .map((item) => String((item as { concepto?: string }).concepto || "—"))
      .join(" · ");
    return `${t("oc.changelog.lineCount", { count: value.length })}: ${concepts}`;
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const ChangeTable: React.FC<{ changes: OcOrderChangeMap }> = ({ changes }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow
          sx={{
            "& th": {
              fontWeight: 700,
              bgcolor: theme.palette.neutral["100"],
              py: 0.5,
            },
          }}
        >
          <TableCell>{t("oc.changelog.colField")}</TableCell>
          <TableCell>{t("oc.changelog.colBefore")}</TableCell>
          <TableCell>{t("oc.changelog.colAfter")}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(changes).map(([field, change]) => (
          <TableRow key={field}>
            <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              {fieldLabel(field, t)}
            </TableCell>
            <TableCell
              sx={{
                color: theme.palette.neutral["600"],
                textDecoration: "line-through",
                wordBreak: "break-word",
              }}
            >
              {formatValue(field, change.old, t)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, wordBreak: "break-word" }}>
              {formatValue(field, change.new, t)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const OcChangeLogDialog: React.FC<OcChangeLogDialogProps> = ({
  open,
  ocId,
  ocNumber,
  onClose,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const { data, isFetching, error } = useGetOcOrderHistoryQuery(ocId ?? "", {
    skip: !open || !ocId,
  });
  const entries: OcOrderHistoryEntry[] = data?.data ?? [];

  const formatWhen = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("oc.changelog.title")}
        {ocNumber && (
          <Typography
            variant="pSmall"
            color={theme.palette.neutral["600"]}
            sx={{ display: "block" }}
          >
            {ocNumber}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography variant="pSmall" color="error">
            {t("oc.changelog.loadError")}
          </Typography>
        ) : entries.length === 0 ? (
          <Typography variant="pSmall" color={theme.palette.neutral["600"]}>
            {t("oc.changelog.empty")}
          </Typography>
        ) : (
          entries.map((entry, index) => (
            <Box key={entry.id} sx={{ py: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  size="small"
                  color={ACTION_COLORS[entry.action] ?? "default"}
                  label={t(`oc.changelog.actions.${entry.action}`)}
                />
                <Typography variant="pSmall" sx={{ fontWeight: 600 }}>
                  {entry.userName ||
                    entry.userEmail ||
                    t("oc.changelog.unknownUser")}
                </Typography>
                <Typography
                  variant="pSmallest"
                  color={theme.palette.neutral["600"]}
                >
                  {formatWhen(entry.createdAt)}
                </Typography>
              </Box>

              {entry.changes && Object.keys(entry.changes).length > 0 ? (
                <ChangeTable changes={entry.changes} />
              ) : (
                <Typography
                  variant="pSmallest"
                  color={theme.palette.neutral["600"]}
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {t("oc.changelog.noDetail")}
                </Typography>
              )}

              {index < entries.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OcChangeLogDialog;
